import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { Component } from '@angular/core'
import { Message } from 'primeng/api'

import { operatesInThisPanel, resolveLandingRoute } from '@data/constants/role-landing-route.constant'
import { FORGOT_PASSWORD_MESSAGES } from '@data/services/modules/auth/forgot-password-outcome.util'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { safeReturnUrl } from '@data/utils/return-url.util'

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent {
  entity: FormGroup
  showMessage = false
  responseMessage: Message[] = []
  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.entity = this.fb.group({
      identifier: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    })
  }

  login() {
    this.showMessage = false
    this.responseMessage = []

    //* B-006 · Un correo va sin espacios y en minúsculas, que es como está guardado; un teléfono se manda
    //* tal cual. El backend ya no distingue mayúsculas, pero así el panel no depende de eso.
    const { identifier, password } = this.entity.value
    const typed = String(identifier ?? '')
    const username = typed.includes('@') ? typed.trim().toLowerCase() : identifier

    this.authService.login({ identifier: username, password }).subscribe((r) => {
      if (r.error) {
        this.responseMessage = [
          {
            summary: r.msg,
            severity: 'error',
            closable: true,
          },
        ]
      } else if (!r.error) {
        const roleName = r.data?.user?.role?.name

        //! Un rol externo (Client, Broker, MasterBroker) SE AUTENTICA bien contra la API —son
        //! usuarios de verdad— pero no opera en este panel. Sin este caso, su landing resuelta
        //! es `/login`, o sea la ruta en la que YA está, y el router ignora navegar a la misma
        //! URL: la pantalla no se movía, no aparecía ningún mensaje y **la sesión se quedaba
        //! guardada en localStorage**. `AuthGuard` los expulsaba al primer movimiento, así que
        //! nunca fue un agujero, pero desde fuera parecía que el botón no hacía nada.
        //*
        //* Se cierra la sesión aquí mismo y se dice a dónde ir. El aviso no distingue rol ni
        //* confirma que la cuenta exista más allá de lo que el propio login ya reveló.
        if (!operatesInThisPanel(roleName)) {
          this.authService.logout()
          this.responseMessage = [
            {
              summary: 'Tu cuenta opera en el canal digital, no en este panel. Entra desde rendafin.com.',
              severity: 'warn',
              closable: true,
            },
          ]
          return
        }

        //* F-145 · Si se llegó aquí con `?returnUrl=` —el botón del correo del comité con la sesión
        //* caducada—, se vuelve a esa pantalla y no a la landing del rol. Solo se honra una ruta del
        //* panel (`safeReturnUrl`): el parámetro lo escribe cualquiera.
        const returnUrl = safeReturnUrl(this.route.snapshot.queryParamMap.get('returnUrl'))
        this.router.navigateByUrl(returnUrl ?? resolveLandingRoute(roleName))
      }
    })
  }

  // ADR-0014: "olvidé mi contraseña" para usuarios internos (por correo). Toma el
  // correo del campo `identifier`. El backend genera una temporal, la envía por
  // correo y la marca para cambio obligatorio. Respuesta genérica (no revela si el
  // correo existe).
  forgotPassword() {
    this.showMessage = false
    this.responseMessage = []

    //* B-006 · Sin espacios y en minúsculas: el teléfono pone la primera letra en mayúscula y así se
    //* manda igual a como está guardado.
    const identifier = String(this.entity.get('identifier')?.value ?? '')
      .trim()
      .toLowerCase()
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)
    if (!isEmail) {
      this.responseMessage = [{ summary: 'Ingresa tu correo para recuperar la contraseña.', severity: 'warn', closable: true }]
      return
    }

    this.authService.forgotPassword(identifier).subscribe((outcome) => {
      this.responseMessage = [{ ...FORGOT_PASSWORD_MESSAGES[outcome], closable: true }]
    })
  }
}
